import { useCallback, useEffect, useMemo, useState } from 'react'
import type { Department } from '../api/catalog'
import {
  approveCommissionProposal,
  createCommissionProposal,
  discardCommissionProposal,
  fetchCommissionProposal,
  fetchCommissionProposals,
  publishCommissionProposal,
  simulateCommissionProposal,
  testCommissionProposal,
  updateCommissionProposal,
  type CommissionProposalDetail,
  type CommissionProposalRequest,
  type CommissionProposalStatus,
  type CommissionProposalSummary,
  type CommissionRole,
  type CommissionSimulation,
} from '../api/commissions'
import {
  Callout,
  DataTable,
  DataTableEmptyRow,
  LoadingState,
  StatCard,
  StatGrid,
  StatusPill,
  FormGrid,
  FormSection,
  Dialog,
  Button,
  IconButton,
  TextField,
  Select,
  useConfirm,
  type PillTone,
} from './ui'

/**
 * F-070 (TASK-0324, REQ-218-001, REQ-219-001): proposte di modifica al listino provvigionale.
 * Una modifica nasce come proposta e segue BOZZA → TEST → SIMULAZIONE su dati storici → APPROVAZIONE
 * (un utente diverso dall'autore) → PUBBLICAZIONE. Finche' non e' pubblicata non e' operativa.
 * Solo Admin: gli importi della simulazione sono riservati.
 */
interface Props {
  departments: Department[]
  roles: CommissionRole[]
  currentUserId: number | null
}

const STATUS_TONE: Record<CommissionProposalStatus, PillTone> = {
  Bozza: 'neutral',
  Verificata: 'info',
  Simulata: 'primary',
  Approvata: 'warning',
  Pubblicata: 'success',
  Scartata: 'neutral',
}

const BASIS_SOURCE_LABELS: Record<string, string> = {
  frozen: 'Base congelata sul contratto',
  product_rule: 'Regola di prodotto',
  bracket_not_computable: 'Operatore a scaglioni (non calcolabile)',
  product_rule_negative: 'Regola di prodotto negativa',
  product_rule_error: 'Regola di prodotto in errore',
  not_determinable: 'Base non determinabile',
}

function formatEuro(value: number): string {
  return value.toLocaleString('it-IT', { style: 'currency', currency: 'EUR' })
}

function formatSigned(value: number): string {
  const text = formatEuro(Math.abs(value))
  return value > 0 ? `+${text}` : value < 0 ? `−${text}` : text
}

function formatPercentage(value: number): string {
  return `${value.toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`
}

function todayIso(): string {
  return new Date().toISOString().slice(0, 10)
}

function addMonthsIso(months: number): string {
  const d = new Date()
  d.setMonth(d.getMonth() + months)
  return d.toISOString().slice(0, 10)
}

interface DraftLine {
  departmentId: string
  commissionRoleId: string
  percentage: string
}

export function CommissionProposalsPanel({ departments, roles, currentUserId }: Props) {
  const [items, setItems] = useState<CommissionProposalSummary[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [selectedId, setSelectedId] = useState<number | null>(null)
  const [creating, setCreating] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      setItems(await fetchCommissionProposals())
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore durante il caricamento delle proposte')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  return (
    <div className="d-flex flex-column gap-3">
      <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
        <div className="md-typescale-body-small text-muted">
          Una modifica al listino nasce come proposta: verifica, simulazione su dati storici, approvazione di un
          secondo utente e pubblicazione. Prima della pubblicazione non e' mai operativa.
        </div>
        <Button variant="tonal" size="sm" onClick={() => setCreating(true)}>
          Nuova versione
        </Button>
      </div>

      {error && <Callout tone="error">{error}</Callout>}

      {loading ? (
        <LoadingState />
      ) : (
        <DataTable>
          <thead>
            <tr>
              <th>#</th>
              <th>Titolo</th>
              <th>Valida dal</th>
              <th>Righe</th>
              <th>Stato</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && <DataTableEmptyRow colSpan={5} icon="request_quote" title="Nessuna proposta" />}
            {items.map(p => (
              <tr key={p.id} role="button" onClick={() => setSelectedId(p.id)}>
                <td>{p.id}</td>
                <td>{p.title}</td>
                <td>{p.validFrom}</td>
                <td>{p.linesCount}</td>
                <td>
                  <StatusPill tone={STATUS_TONE[p.status]}>{p.status}</StatusPill>
                </td>
              </tr>
            ))}
          </tbody>
        </DataTable>
      )}

      {creating && (
        <ProposalFormModal
          departments={departments}
          roles={roles}
          existing={null}
          onClose={() => setCreating(false)}
          onSaved={id => {
            setCreating(false)
            void load()
            setSelectedId(id)
          }}
        />
      )}

      {selectedId !== null && (
        <ProposalDetailModal
          id={selectedId}
          departments={departments}
          roles={roles}
          currentUserId={currentUserId}
          onClose={() => setSelectedId(null)}
          onChanged={() => void load()}
        />
      )}
    </div>
  )
}

function ProposalFormModal({
  departments,
  roles,
  existing,
  onClose,
  onSaved,
}: {
  departments: Department[]
  roles: CommissionRole[]
  existing: CommissionProposalDetail | null
  onClose: () => void
  onSaved: (id: number) => void
}) {
  const [title, setTitle] = useState(existing?.title ?? '')
  const [reason, setReason] = useState(existing?.reason ?? '')
  const [validFrom, setValidFrom] = useState(existing?.validFrom ?? todayIso())
  const [lines, setLines] = useState<DraftLine[]>(
    existing
      ? existing.lines.map(l => ({
          departmentId: String(l.departmentId),
          commissionRoleId: String(l.commissionRoleId),
          percentage: String(l.percentage),
        }))
      : [{ departmentId: '', commissionRoleId: '', percentage: '' }],
  )
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  const activeDepartments = useMemo(
    () => [...departments].filter(d => d.isActive).sort((a, b) => a.name.localeCompare(b.name)),
    [departments],
  )
  const sortedRoles = useMemo(
    () => [...roles].sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name)),
    [roles],
  )

  function updateLine(index: number, patch: Partial<DraftLine>): void {
    setLines(prev => prev.map((l, i) => (i === index ? { ...l, ...patch } : l)))
  }

  async function handleSave(): Promise<void> {
    setError('')
    if (!title.trim()) return setError('Il titolo e\' obbligatorio.')
    if (!reason.trim()) return setError('La motivazione e\' obbligatoria.')
    if (!validFrom) return setError('Seleziona la data di validita\'.')

    const parsed: CommissionProposalRequest['lines'] = []
    for (const [i, l] of lines.entries()) {
      const pct = Number(l.percentage.replace(',', '.'))
      if (!l.departmentId || !l.commissionRoleId || !l.percentage.trim() || Number.isNaN(pct) || pct < 0 || pct > 100)
        return setError(`Riga ${i + 1}: scegli settore, ruolo e una percentuale tra 0 e 100.`)
      parsed.push({ departmentId: Number(l.departmentId), commissionRoleId: Number(l.commissionRoleId), percentage: pct })
    }
    if (parsed.length === 0) return setError('Aggiungi almeno una riga.')

    const body: CommissionProposalRequest = { title: title.trim(), reason: reason.trim(), validFrom, lines: parsed }
    setSaving(true)
    try {
      const saved = existing
        ? await updateCommissionProposal(existing.id, body)
        : await createCommissionProposal(body)
      onSaved(saved.id)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore durante il salvataggio della proposta')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog
      open
      onClose={onClose}
      title={existing ? `Modifica proposta #${existing.id}` : 'Nuova versione del listino'}
      as="form"
      onSubmit={e => { e.preventDefault(); void handleSave() }}
      actions={
        <>
          <Button variant="text" onClick={onClose}>
            Annulla
          </Button>
          <Button type="submit" variant="text" disabled={saving}>
            {saving ? 'Salvataggio...' : 'Salva proposta'}
          </Button>
        </>
      }
    >
      {error && <Callout tone="error">{error}</Callout>}
      {existing && existing.status !== 'Bozza' && (
        <Callout tone="warning">
          Salvando, la proposta torna in Bozza e test, simulazione e approvazione vanno ripetuti.
        </Callout>
      )}
      <FormSection title="Proposta">
        <TextField label="Titolo *" maxLength={150} value={title} onChange={e => setTitle(e.target.value)} />
        <TextField
          type="date"
          label="Valida dal *"
          min={todayIso()}
          value={validFrom}
          onChange={e => setValidFrom(e.target.value)}
        />
        <TextField
          className="md-span-full"
          multiline
          rows={2}
          label="Motivazione *"
          maxLength={500}
          value={reason}
          onChange={e => setReason(e.target.value)}
        />
      </FormSection>

      <FormSection title="Celle da modificare" columns={1}>
        <div className="d-flex flex-column gap-4">
          {lines.map((line, index) => (
            <FormGrid key={index}>
              <Select
                className="md-span-full"
                label="Settore"
                value={line.departmentId}
                onChange={e => updateLine(index, { departmentId: e.target.value })}
              >
                <option value="">Settore…</option>
                {activeDepartments.map(d => (
                  <option key={d.id} value={d.id}>
                    {d.name}
                  </option>
                ))}
              </Select>
              <Select
                label="Ruolo"
                value={line.commissionRoleId}
                onChange={e => updateLine(index, { commissionRoleId: e.target.value })}
              >
                <option value="">Ruolo…</option>
                {sortedRoles.map(r => (
                  <option key={r.id} value={r.id}>
                    {r.name}
                  </option>
                ))}
              </Select>
              <div className="d-flex gap-2 align-items-end">
                <TextField
                  className="flex-grow-1"
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                  label="%"
                  placeholder="%"
                  value={line.percentage}
                  onChange={e => updateLine(index, { percentage: e.target.value })}
                />
                <IconButton
                  icon="close"
                  size="sm"
                  aria-label="Rimuovi riga"
                  disabled={lines.length === 1}
                  onClick={() => setLines(prev => prev.filter((_, i) => i !== index))}
                />
              </div>
            </FormGrid>
          ))}
        </div>
        <div>
          <Button
            variant="outlined"
            size="sm"
            onClick={() => setLines(prev => [...prev, { departmentId: '', commissionRoleId: '', percentage: '' }])}
          >
            Aggiungi riga
          </Button>
        </div>
      </FormSection>
    </Dialog>
  )
}

function ProposalDetailModal({
  id,
  departments,
  roles,
  currentUserId,
  onClose,
  onChanged,
}: {
  id: number
  departments: Department[]
  roles: CommissionRole[]
  currentUserId: number | null
  onClose: () => void
  onChanged: () => void
}) {
  const [proposal, setProposal] = useState<CommissionProposalDetail | null>(null)
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)
  const [editing, setEditing] = useState(false)
  const [from, setFrom] = useState(addMonthsIso(-12))
  const [to, setTo] = useState(todayIso())
  const [discardReason, setDiscardReason] = useState('')
  const askConfirm = useConfirm()

  const reload = useCallback(async () => {
    try {
      setProposal(await fetchCommissionProposal(id))
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore durante il caricamento della proposta')
    }
  }, [id])

  useEffect(() => {
    void reload()
  }, [reload])

  const departmentName = (deptId: number) => departments.find(d => d.id === deptId)?.name ?? `Settore ${deptId}`
  const roleName = (roleId: number) => roles.find(r => r.id === roleId)?.name ?? `Ruolo ${roleId}`

  async function run(action: () => Promise<unknown>): Promise<void> {
    setError('')
    setBusy(true)
    try {
      await action()
      await reload()
      onChanged()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Operazione non riuscita')
    } finally {
      setBusy(false)
    }
  }

  if (!proposal) {
    return (
      <Dialog
        open
        onClose={onClose}
        title={`Proposta #${id}`}
        actions={
          <Button variant="text" dismiss onClick={onClose}>
            Chiudi
          </Button>
        }
      >
        {error ? <Callout tone="error">{error}</Callout> : <LoadingState />}
      </Dialog>
    )
  }

  if (editing) {
    return (
      <ProposalFormModal
        departments={departments}
        roles={roles}
        existing={proposal}
        onClose={() => setEditing(false)}
        onSaved={() => {
          setEditing(false)
          void reload()
          onChanged()
        }}
      />
    )
  }

  const status = proposal.status
  const isAuthor = currentUserId !== null && proposal.createdBy === currentUserId
  const open = status !== 'Pubblicata' && status !== 'Scartata'

  return (
    <Dialog
      open
      onClose={onClose}
      size="lg"
      as={open ? 'form' : 'div'}
      onSubmit={(e) => {
        e.preventDefault()
        void (async () => {
          const ok = await askConfirm({
            title: 'Scartare la proposta?',
            message: `La proposta #${id} verrà scartata con la motivazione indicata e non sarà più modificabile.`,
            confirmLabel: 'Scarta',
            destructive: true,
          })
          if (ok) await run(() => discardCommissionProposal(id, discardReason.trim()))
        })()
      }}
      title={`#${proposal.id} — ${proposal.title}`}
      actions={
        open ? (
          <>
            <Button variant="text" disabled={busy} onClick={() => setEditing(true)}>
              Modifica
            </Button>
            <Button
              variant="text"
              type="submit"
              disabled={busy || !discardReason.trim()}
            >
              Scarta
            </Button>
          </>
        ) : undefined
      }
    >
      {error && <Callout tone="error">{error}</Callout>}
      <div>
        <StatusPill tone={STATUS_TONE[status]}>{status}</StatusPill>
      </div>

      <div className="d-flex flex-column gap-1">
        <p className="m-0">
          <strong>Motivazione:</strong> {proposal.reason}
        </p>
        <p className="m-0 md-typescale-body-small text-muted">
          Valida dal {proposal.validFrom} · autore #{proposal.createdBy ?? '—'}
          {proposal.approvedBy !== null && ` · approvata da #${proposal.approvedBy}`}
          {proposal.discardReason && ` · scartata: ${proposal.discardReason}`}
        </p>
      </div>

      <FormSection title="Celle" columns={1}>
        <div className="md-dialog__bleed">
          <DataTable variant="plain">
            <thead>
              <tr>
                <th>Settore</th>
                <th>Ruolo</th>
                <th className="md-num">Oggi</th>
                <th className="md-num">Proposta</th>
              </tr>
            </thead>
            <tbody>
              {proposal.lines.map(l => (
                <tr key={`${l.departmentId}-${l.commissionRoleId}`}>
                  <td>{departmentName(l.departmentId)}</td>
                  <td>{roleName(l.commissionRoleId)}</td>
                  <td className="md-num">
                    {l.currentPercentage !== null && l.currentPercentage !== undefined ? (
                      formatPercentage(l.currentPercentage)
                    ) : (
                      <span className="text-muted">—</span>
                    )}
                  </td>
                  <td className="md-typescale-emphasized-body-medium md-num">{formatPercentage(l.percentage)}</td>
                </tr>
              ))}
            </tbody>
          </DataTable>
        </div>
      </FormSection>

      <FormSection title="1. Verifica" columns={1}>
        {proposal.test ? (
          proposal.test.passed ? (
            <Callout tone="success">Verifica superata.</Callout>
          ) : (
            <Callout tone="error">
              <ul className="mb-0">
                {proposal.test.issues.map((i, n) => (
                  <li key={n}>{i.message}</li>
                ))}
              </ul>
            </Callout>
          )
        ) : (
          <div className="text-muted md-typescale-body-small">Non ancora eseguita.</div>
        )}
        {(status === 'Bozza' || status === 'Verificata') && (
          <div>
            <Button
              variant="outlined"
              size="sm"
              disabled={busy}
              onClick={() => void run(() => testCommissionProposal(id))}
            >
              Esegui verifica
            </Button>
          </div>
        )}
      </FormSection>

      <FormSection title="2. Simulazione su dati storici">
        {(status === 'Verificata' || status === 'Simulata') && (
          <>
            <TextField type="date" label="Dal" value={from} onChange={e => setFrom(e.target.value)} />
            <TextField type="date" label="Al" value={to} onChange={e => setTo(e.target.value)} />
            <div className="md-span-full d-flex gap-2 align-items-center flex-wrap">
              <Button
                variant="outlined"
                size="sm"
                disabled={busy}
                onClick={() => void run(() => simulateCommissionProposal(id, { from, to }))}
              >
                {status === 'Simulata' ? 'Ripeti simulazione' : 'Simula'}
              </Button>
              <span className="md-typescale-body-small text-muted">Finestra massima 24 mesi, contratti Attivata.</span>
            </div>
          </>
        )}
        <div className="md-span-full">
          {proposal.simulation ? (
            <SimulationView simulation={proposal.simulation} />
          ) : (
            <div className="text-muted md-typescale-body-small">Non ancora eseguita.</div>
          )}
        </div>
      </FormSection>

      <FormSection title="3. Approvazione e pubblicazione" columns={1}>
        <div className="d-flex gap-2 flex-wrap align-items-center">
          {status === 'Simulata' && (
            <>
              <Button
                variant="tonal"
                size="sm"
                disabled={busy || isAuthor}
                title={isAuthor ? "L'approvatore deve essere diverso dall'autore" : undefined}
                onClick={() => void run(() => approveCommissionProposal(id))}
              >
                Approva
              </Button>
              {isAuthor && <span className="md-typescale-body-small text-muted">Serve un secondo utente (four-eyes).</span>}
            </>
          )}
          {status === 'Approvata' && (
            <Button
              variant="tonal"
              size="sm"
              disabled={busy}
              onClick={() => void run(() => publishCommissionProposal(id))}
            >
              Pubblica
            </Button>
          )}
          {status === 'Pubblicata' && (
            <span className="md-typescale-body-small text-muted">
              Pubblicata il {proposal.publishedAt?.slice(0, 10)}: la versione precedente e' chiusa alla data di validita'.
            </span>
          )}
        </div>
      </FormSection>

      {open && (
        <FormSection title="4. Scarto" columns={1}>
          <TextField
            label="Motivo dello scarto"
            value={discardReason}
            onChange={e => setDiscardReason(e.target.value)}
          />
        </FormSection>
      )}
    </Dialog>
  )
}

function SimulationView({ simulation }: { simulation: CommissionSimulation }) {
  const noBasisTotal = simulation.contractsWithoutBasis
  return (
    <div className="d-flex flex-column gap-3">
      <StatGrid>
        <StatCard label="Provvigioni attuali" value={formatEuro(simulation.oldTotal)} />
        <StatCard label="Con la proposta" value={formatEuro(simulation.newTotal)} />
        <StatCard label="Differenza" value={formatSigned(simulation.difference)} />
        <StatCard label="Contratti interessati" value={`${simulation.contractsAffected} / ${simulation.contractsComputed}`} />
        <StatCard label="Agenti interessati" value={String(simulation.agentsAffected)} />
      </StatGrid>
      <div className="md-typescale-body-small text-muted">
        Finestra {simulation.window.from} — {simulation.window.to} · {simulation.contractsConsidered} contratti nel
        periodo. Data di riferimento: firma, altrimenti creazione. Simulazione sola lettura: lo storico non viene
        ricalcolato.
      </div>
      {(noBasisTotal > 0 || simulation.contractsWithoutAgent > 0 || simulation.contractsWithoutChain > 0) && (
        <Callout tone="warning">
          Non calcolabili e quindi esclusi dai totali: {noBasisTotal} senza base di calcolo,{' '}
          {simulation.contractsWithoutAgent} senza agente, {simulation.contractsWithoutChain} senza catena provvigionale
          risolvibile.
          {Object.keys(simulation.basisSources).length > 0 && (
            <div className="mt-1">
              Origine della base:{' '}
              {Object.entries(simulation.basisSources)
                .map(([k, n]) => `${BASIS_SOURCE_LABELS[k] ?? k}: ${n}`)
                .join(' · ')}
            </div>
          )}
        </Callout>
      )}

      {simulation.perAgent.length > 0 && (
        <>
          <div className="md-typescale-emphasized-body-small">Per agente</div>
          <div className="md-dialog__bleed">
            <DataTable variant="plain">
              <thead>
                <tr>
                  <th>Utente</th>
                  <th className="md-num">Attuale</th>
                  <th className="md-num">Nuova</th>
                  <th className="md-num">Differenza</th>
                  <th className="md-num">Contratti</th>
                </tr>
              </thead>
              <tbody>
                {simulation.perAgent.map(a => (
                  <tr key={a.userId}>
                    <td>#{a.userId}</td>
                    <td className="md-num">{formatEuro(a.oldAmount)}</td>
                    <td className="md-num">{formatEuro(a.newAmount)}</td>
                    <td className="md-num">{formatSigned(a.difference)}</td>
                    <td className="md-num">{a.contracts}</td>
                  </tr>
                ))}
              </tbody>
            </DataTable>
          </div>
        </>
      )}

      {simulation.sample.length > 0 && (
        <details>
          <summary className="md-typescale-body-small">Campione di {simulation.sample.length} contratti</summary>
          <div className="md-dialog__bleed">
            <DataTable variant="plain">
              <thead>
                <tr>
                  <th>Pratica</th>
                  <th>Data</th>
                  <th className="md-num">Base</th>
                  <th className="md-num">Attuale</th>
                  <th className="md-num">Nuova</th>
                  <th className="md-num">Differenza</th>
                </tr>
              </thead>
              <tbody>
                {simulation.sample.map(c => (
                  <tr key={c.contractId}>
                    <td>#{c.contractId}</td>
                    <td>{c.referenceDate}</td>
                    <td className="md-num">{formatEuro(c.basis)}</td>
                    <td className="md-num">{formatEuro(c.oldTotal)}</td>
                    <td className="md-num">{formatEuro(c.newTotal)}</td>
                    <td className="md-num">{formatSigned(c.difference)}</td>
                  </tr>
                ))}
              </tbody>
            </DataTable>
          </div>
        </details>
      )}
    </div>
  )
}
